import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, AlertCircle, Phone, Mail, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import SEOHead from '../components/SEO/SEOHead';
import { useBrand } from '../components/BrandProvider';

const AssuranceMesurePage = () => {
  const brand = useBrand();

  const eligibleProducts = [
    { name: "Toile De Store", path: "/", description: "Store banne classique" },
    { name: "Toile de Store Droit", path: "/store-droit", description: "Store à projection verticale" },
    { name: "Toile de Store à Double Pente", path: "/store-double-pente", description: "Store double pente" },
    { name: "Lambrequin Seul", path: "/lambrequin", description: "Lambrequin de finition" },
  ];

  const nonEligibleProducts = [
    { name: "Toile De Pergola", path: "/pergola" },
    { name: "Brise-Vue De Balcon", path: "/brise-vue" },
    { name: "Toile de Store au Mètre", path: "/toile-au-metre" },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={`Assurance Prise de Mesure - ${brand.name}`}
        description="Notre garantie exclusive couvre vos erreurs de mesure. Nous refabriquons gratuitement votre toile si les dimensions ne correspondent pas."
        url="/assurance-mesure"
      />

      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Logo showTagline={true} clickable={true} />
        </div>
      </header>

      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-celadon to-emerald-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 p-4 rounded-full">
              <Shield className="w-16 h-16" />
            </div>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Assurance Prise de Mesure
          </h1>
          <p className="text-xl sm:text-2xl opacity-90">
            Incluse gratuitement avec vos toiles sur mesure
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Introduction */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="text-celadon" />
            Qu'est-ce que l'Assurance Prise de Mesure ?
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed">
            Prendre les mesures d'un store banne peut être délicat. C'est pourquoi nous avons créé cette garantie exclusive : 
            <strong> si vous faites une erreur de mesure, nous refabriquons votre toile gratuitement</strong>.
          </p>
        </div>

        {/* How it works */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Comment ça fonctionne ?</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-celadon text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">1</div>
              <h3 className="font-semibold mb-2">Commandez</h3>
              <p className="text-gray-600 text-sm">Passez votre commande avec les dimensions que vous avez mesurées</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-celadon text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">2</div>
              <h3 className="font-semibold mb-2">Vérifiez</h3>
              <p className="text-gray-600 text-sm">À réception, si la toile ne correspond pas à votre store, contactez-nous</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-12 h-12 bg-celadon text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">3</div>
              <h3 className="font-semibold mb-2">Refabrication</h3>
              <p className="text-gray-600 text-sm">Nous refabriquons gratuitement votre toile aux bonnes dimensions</p>
            </div>
          </div>
        </div>

        {/* Eligible Products */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <CheckCircle className="text-celadon" />
            Produits éligibles
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {eligibleProducts.map((product) => (
              <Link 
                key={product.name}
                to={product.path}
                className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
              >
                <Shield className="w-6 h-6 text-celadon flex-shrink-0" />
                <div>
                  <p className="font-semibold text-gray-900">{product.name}</p>
                  <p className="text-sm text-gray-600">{product.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Non-eligible Products */}
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <AlertCircle className="text-orange-500" />
            Produits non éligibles
          </h2>
          <p className="text-gray-600 mb-4">
            Les produits suivants ne sont pas couverts par l'Assurance Prise de Mesure :
          </p>
          <div className="grid sm:grid-cols-3 gap-4">
            {nonEligibleProducts.map((product) => (
              <Link 
                key={product.name}
                to={product.path}
                className="flex items-center gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <span className="text-gray-400">—</span>
                <p className="text-gray-700">{product.name}</p>
              </Link>
            ))}
          </div>
        </div>

        {/* Conditions */}
        <div className="mb-10 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Conditions d'application</h2>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-celadon mt-0.5 flex-shrink-0" />
              <span>La demande de refabrication doit être effectuée dans les <strong>30 jours</strong> suivant la réception</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-celadon mt-0.5 flex-shrink-0" />
              <span>La toile originale doit nous être retournée (frais de retour à votre charge)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-celadon mt-0.5 flex-shrink-0" />
              <span>L'assurance couvre <strong>une seule refabrication</strong> par commande</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-celadon mt-0.5 flex-shrink-0" />
              <span>Les options et coloris restent identiques à la commande initiale</span>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div className="bg-gray-900 text-white rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Besoin de faire une réclamation ?</h2>
          <p className="text-gray-300 mb-6">
            Notre équipe est à votre disposition pour traiter votre demande de refabrication.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href={`tel:${brand.contact.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-celadon hover:bg-celadon/90 text-white px-6 py-3 rounded-lg transition-colors"
            >
              <Phone className="w-5 h-5" />
              {brand.contact.phoneDisplay}
            </a>
            <a 
              href={`mailto:${brand.contact.email}?subject=Demande de refabrication - Assurance Prise de Mesure`}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 rounded-lg transition-colors"
            >
              <Mail className="w-5 h-5" />
              Nous contacter
            </a>
          </div>
        </div>

        {/* Back Link */}
        <div className="mt-10 text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-celadon hover:text-celadon/80 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AssuranceMesurePage;
